// The migration kit: each customer's steps, with their own project id, slug and backend URL filled
// in. Pure (no DOM), so it's unit-tested in kit.test.ts.

export type Platform = "web" | "ios" | "android" | "server";

export interface KitProject {
  name: string;
  slug: string;
  /** "firebase" (the migration kit) or "fresh" (a new app from a starter). */
  source: "firebase" | "fresh";
  /** Fresh projects: the starter they were created from (blank, social). */
  starter?: string;
  firebaseProjectId: string;
  platforms: Platform[];
  keepFirebaseAuth: boolean;
  apiUrl?: string;
}

export interface KitBlock {
  /** Shown above the code. */
  text?: string;
  code?: string;
  /** Code language hint for the label (shell, ts, kotlin…). */
  lang?: string;
}

export interface KitStep {
  id: string;
  title: string;
  summary: string;
  blocks: KitBlock[];
  /** Steps with a checkbox the customer ticks when done. */
  checklist?: { id: string; label: string }[];
}

export const tenantUrl = (p: Pick<KitProject, "slug" | "apiUrl">) => p.apiUrl ?? `https://${p.slug}.estra.cloud`;
/** The backend's host for display: an attached project's own domain, else <slug>.estra.cloud. */
export const backendHost = (doc: object) => tenantUrl(doc as Pick<KitProject, "slug" | "apiUrl">).replace(/^https?:\/\//, "");

/** The import CLI: published as @estra/import, binary estra-import. */
const CLI = "npx @estra/import";

export function schemaStep(p: KitProject): KitStep {
  return {
    id: "schema",
    title: "Draft your schema",
    summary: "Estra stores each collection as a typed table. The importer reads your Firestore data and drafts the schema; you review it and upload it with your rules.",
    blocks: [
      {
        text: "In your app's folder, with a Firebase service-account key. The key stays on your machine; Estra never sees it.",
        lang: "shell",
        code: `${CLI} schema --project ${p.firebaseProjectId} \\\n  --credentials ./service-account.json \\\n  --indexes firestore.indexes.json \\\n  --out schema.ts`,
      },
      {
        text: "Review schema.ts before uploading. Every field your app writes must be declared, because writes naming an unknown field are refused. Fields the sample didn't see are listed as notes.",
      },
    ],
  };
}

export function steps(p: KitProject): KitStep[] {
  const url = tenantUrl(p);
  const creds = `--project ${p.firebaseProjectId} \\\n  --credentials ./service-account.json`;
  const target = `--target $ESTRA_URL --key $ESTRA_KEY`;
  const cmd = (verb: string, ...rest: string[]) => [`${CLI} ${verb} ${creds}`, ...rest].join(" \\\n  ");
  const out: KitStep[] = [];

  out.push({
    id: "data",
    title: "Copy your data",
    summary: "Read-only on Firebase. Run it as often as you like: it resumes, and later runs copy only what changed.",
    blocks: [
      { lang: "shell", code: `export ESTRA_URL=${url}\nexport ESTRA_KEY=…   # the service key from the previous step` },
      { text: "Check first, then copy and compare:", lang: "shell", code: [cmd("data", "--schema schema.ts", target, "--dry-run"), cmd("data", "--schema schema.ts", target), cmd("verify", "--schema schema.ts", target, "--random --sample 200")].join("\n\n") },
      { text: "Until you switch over, top up with what changed since the last run. Each run prints the cutoff to use next.", lang: "shell", code: cmd("data", "--schema schema.ts", target, "--since <cutoff from the last run>") },
    ],
    checklist: [
      { id: "dry-run", label: "Dry run shows no dropped fields" },
      { id: "copied", label: "Data copied" },
      { id: "verified", label: "verify reports 0 mismatched" },
    ],
  });

  out.push({
    id: "users",
    title: p.keepFirebaseAuth ? "Sign-in (stays on Firebase)" : "Move your users",
    summary: p.keepFirebaseAuth
      ? "Your backend accepts your Firebase ID tokens, so users stay signed in and nothing about sign-in changes. Move it later if you like."
      : "Accounts and password hashes come across, so passwords keep working. Users sign in once on the new backend.",
    blocks: [
      {
        text: p.keepFirebaseAuth ? "When you move sign-in later, import users like this:" : "Import users and save Firebase's hash parameters:",
        lang: "shell",
        code: cmd("users", target, "--hash-config-out hash-config.json"),
      },
      { text: "Then paste hash-config.json into “Password hashes” on this page. It's stored write-only and applied to your backend." },
    ],
  });

  const sw: KitBlock[] = [];
  if (p.platforms.includes("web")) {
    sw.push(
      { text: "Web: install the client and point Firebase's Firestore import at Estra's compatible build.", lang: "shell", code: "npm i @estra/client" },
      {
        text: "In your bundler config (Vite shown; webpack and Next.js use resolve.alias the same way):",
        lang: "ts",
        code: p.keepFirebaseAuth
          ? `resolve: { alias: { "firebase/firestore": "@estra/client/compat/firestore" } }`
          : `resolve: {\n  alias: {\n    "firebase/app": "@estra/client/compat/app",\n    "firebase/auth": "@estra/client/compat/auth",\n    "firebase/firestore": "@estra/client/compat/firestore",\n    "firebase/storage": "@estra/client/compat/storage",\n  },\n}`,
      },
      p.keepFirebaseAuth
        ? {
            text: "Where you create Firestore, hand it your Firebase user's token:",
            lang: "ts",
            code: `import { getAuth, onIdTokenChanged } from "firebase/auth";\nimport { initializeFirestore } from "firebase/firestore";\n\nconst auth = getAuth(app);\nconst db = initializeFirestore(app, {\n  estraUrl: "${url}",\n  getToken: () => auth.currentUser?.getIdToken() ?? null,\n  onTokenChange: (cb) => onIdTokenChanged(auth, cb),\n});`,
          }
        : { text: "Where you initialize Firebase:", lang: "ts", code: `const app = initializeApp({ ...firebaseConfig, estraUrl: "${url}" });` },
    );
  }
  if (p.platforms.includes("ios") || p.platforms.includes("android")) {
    sw.push({
      text: "iOS and Android (Firestore REST): swap the host, keep the paths. Estra serves Firestore's REST API as is.",
      lang: "text",
      code: `https://firestore.googleapis.com/v1/projects/${p.firebaseProjectId}/…\n→ ${url}/v1/projects/${p.firebaseProjectId}/…` +
        (p.keepFirebaseAuth ? "" : `\n\nhttps://identitytoolkit.googleapis.com/v1/…  →  ${url}/v1/…\nhttps://securetoken.googleapis.com/v1/token  →  ${url}/v1/token`),
    });
    sw.push({ text: "Using the Firebase iOS or Android SDK rather than REST? Talk to us first: native SDKs need Estra's Kotlin Multiplatform SDK or the REST route above." });
  }
  if (p.platforms.includes("server")) {
    sw.push({
      text: "Servers and scripts: @estra/admin-node mirrors firebase-admin, so it's mostly an import swap.",
      lang: "ts",
      code: `import { initializeApp, getFirestore } from "@estra/admin-node";\n\ninitializeApp({ url: "${url}", serviceKey: process.env.ESTRA_SERVICE_KEY! });\nconst db = getFirestore();`,
    });
  }
  out.push({ id: "switch", title: "Switch your app", summary: "One host change per platform. Ship it to a preview build first and click through.", blocks: sw });

  out.push({
    id: "cutover",
    title: "Cut over",
    summary: "The order that worked for us in production. Firebase is never written to, so it stays your rollback.",
    blocks: [],
    checklist: [
      { id: "rehearse", label: "Rehearse: preview build against your Estra backend, every screen works" },
      { id: "freeze", label: "Pick a quiet window and pause writes (cron jobs, admin tools)" },
      { id: "topup", label: "Final top-up with --since, then verify" },
      { id: "ship", label: "Ship the switched web deploy and app builds" },
      { id: "rollback", label: "Leave Firestore untouched for two weeks as your rollback" },
    ],
  });
  return out;
}

/** The steps of a project that started from a starter rather than from Firebase. Step 1 (the backend
 *  and its key) is the page's own section; these follow it. */
export function freshSteps(p: KitProject): KitStep[] {
  const url = tenantUrl(p);
  const starter = p.starter ?? "blank";
  const plats = p.platforms.join(",");
  const out: KitStep[] = [];

  const get: KitBlock[] = [
    {
      text: "In your app's repository. It writes the schema and rules your backend was built with, plus a client example for each platform you picked. Nothing is sent anywhere.",
      lang: "shell",
      code: `npm create @estra@latest estra -- --url ${url} --starter ${starter} --platforms ${plats}`,
    },
  ];
  if (p.platforms.includes("web")) {
    get.push({
      text: "Web: install the client, then import db and auth from the generated examples/web/estra.ts. Email links land on examples/web/auth-action.ts; serve it at /auth/action on your first web origin.",
      lang: "shell",
      code: "npm i @estra/client",
    });
  }
  if (p.platforms.includes("server")) {
    get.push({
      text: "Servers and scripts: @estra/admin-node with the service key from step 1 (ESTRA_KEY in .env, never in an app).",
      lang: "shell",
      code: "npm i @estra/admin-node",
    });
  }
  if (p.platforms.includes("ios") || p.platforms.includes("android")) {
    get.push({ text: "iOS and Android use the Kotlin Multiplatform SDK; examples/mobile/Estra.kt shows the calls. Until it is on Maven Central, build it from the Estra repository (sdks/kmp), or talk to us." });
  }
  out.push({
    id: "code",
    title: "Get the code",
    summary: "One command gives you the files; the examples do a sign-up, a write and a live query against your backend.",
    blocks: get,
    checklist: [{ id: "ran", label: "The example ran and the write showed up" }],
  });

  out.push({
    id: "signin",
    title: "Sign-in",
    summary: "Email link and password sign-in work now, with mail from no-reply@estra.cloud. Google, Apple and GitHub work once you switch them on in “Sign-in providers” below.",
    blocks: [
      { lang: "ts", code: `await auth.signUp(email, password, displayName);   // or auth.signIn(email, password)\nawait auth.sendSignInLink(email);                   // password-less; finished by auth-action.ts\nauth.onAuthStateChanged((user) => render(user));` },
      { text: "Google, Apple or GitHub in a popup. Call it straight from the click handler: browsers block popups opened after an await.", lang: "ts", code: `button.onclick = () => auth.signInWithPopup("google.com");   // "apple.com", "github.com"` },
      { text: "Mail from your own domain instead? Add it in the Email section below and it is used for sign-in links too." },
    ],
  });

  out.push({
    id: "schema",
    title: "Change your schema",
    summary: "Edit schema.ts and firestore.rules in your repository, then push them. The backend migrates its database and switches over in seconds, with no deploy and no downtime.",
    blocks: [
      { text: "Once, in the folder npm create @estra wrote (it has the package.json the CLI needs): install, then put the service key from step 1 in .env as ESTRA_KEY=…, with an editor so it stays out of your shell history. .env is git-ignored.", lang: "shell", code: "cd estra\nnpm install" },
      {
        text: "Then after every change. --dry-run shows the SQL first. Removing a field or a collection deletes its data, so the push refuses it until you add --allow-destructive.",
        lang: "shell",
        code: "npm run push:check\nnpm run push",
      },
      { text: "No terminal handy? Upload the two files in step 2 and apply them; we push them for you within minutes and email you when they're live." },
    ],
  });

  out.push({
    id: "launch",
    title: "Before launch",
    summary: "The short list.",
    blocks: [],
    checklist: [
      { id: "rules", label: "Rules reviewed: every collection has a match block, nothing is world-writable" },
      { id: "origins", label: "Web origins set to your real domains (edit the project)" },
      { id: "key", label: "The service key is in your server's secrets, not in any app bundle" },
      { id: "mail", label: "A sending domain verified in Email, so sign-in mail comes from you" },
    ],
  });
  return out;
}

/**
 * schema.ts as a function body for `new Function("collection", "defineSchema", "f", body)`: the
 * @estra/schema import becomes those parameters, exports become plain declarations, and the body
 * returns `schema`. The dashboard runs it in a Web Worker (schema-worker.ts) to turn an upload into
 * schema.json, because the backend takes JSON and nothing server-side runs customer code.
 * TypeScript-only syntax (type annotations) isn't supported; precheckSchema has run first.
 */
export function schemaProgram(src: string): string {
  const body = src
    .replace(/^\s*import\s[^;]*?from\s*["']@estra\/schema["'];?[ \t]*$/gm, "")
    .replace(/^\s*export\s*\{\s*plugins\s*\}\s*from\s*["'][^"']+["'];?[ \t]*$/gm, "")
    .replace(/^([ \t]*)export\s+(const|let|function)\b/gm, "$1$2");
  return `"use strict";\n${body}\n;return typeof schema === "undefined" ? undefined : schema;`;
}

/** A light check before upload; the full check runs when we provision. */
export function precheckSchema(src: string): string | null {
  if (!/export const schema\s*=\s*defineSchema\(/.test(src)) return "schema.ts must contain `export const schema = defineSchema(…)`. Generate it with the command above.";
  const imports = [...src.matchAll(/^\s*import\s[^;]*?from\s*["']([^"']+)["']/gm)].map((m) => m[1]);
  if (imports.some((m) => m !== "@estra/schema")) return `schema.ts may only import from "@estra/schema" (found ${imports.join(", ")}).`;
  return null;
}

export const SLUG_RE = /^[a-z][a-z0-9-]{1,28}[a-z0-9]$/;
export const RESERVED = ["www", "api", "app", "admin", "docs", "mail", "status", "blog", "help", "dashboard", "estra"];
export const slugify = (name: string) =>
  name.toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").replace(/^[^a-z]+/, "").slice(0, 30).replace(/-+$/, "");
